'use strict';

import React from 'react';
import {ObjectSchema as JoiObjectSchema} from 'joi';

import User from '~/shared/src/model/User';

import {AppDispatch} from '~/frontend/src/store';

/**
 * That are the components to handle as panels (tabs)
 */
import BusinessProcessDesignerView, {ComponentProps as BusinessProcessDesignerViewComponentProps} from '~/frontend/src/modules/panels/BusinessProcessDesignerView';
import ChartDesignerView, {ComponentProps as ChartDesignerViewComponentProps} from '~/frontend/src/modules/panels/ChartDesignerView';
import FinderDetail, {ComponentProps as FinderDetailComponentProps} from '~/frontend/src/modules/panels/Finder/detail';
import FinderOverview, {ComponentProps as FinderOverviewComponentProps} from '~/frontend/src/modules/panels/Finder/overview';
import Form, {ComponentProps as FormComponentProps} from '~/frontend/src/modules/panels/FormView';
import FormDesignerView, {ComponentProps as FormDesignerViewComponentProps} from '~/frontend/src/modules/panels/FormDesignerView';
import GraphDesignerView, {ComponentProps as GraphDesignerViewComponentProps} from '~/frontend/src/modules/panels/GraphDesignerView';
import MapSearchView, {ComponentProps as MapSearchViewComponentProps} from '~/frontend/src/modules/panels/MapSearchView';
import MetaModelDesignerView, {ComponentProps as MetaModelDesignerViewProps} from '~/frontend/src/modules/panels/MetaModelDesignerView';
import Overview, {ComponentProps as OverviewComponentProps} from '~/frontend/src/modules/panels/OverviewView';
import OverviewDesignerView, {ComponentProps as OverviewDesignerViewComponentProps} from '~/frontend/src/modules/panels/OverviewDesignerView';
import QueryExecute, {ComponentProps as QueryExecuteComponentProps} from '~/frontend/src/modules/panels/QueryExecute';
import TreeDesignerView, {ComponentProps as TreeDesignerViewComponentProps} from '~/frontend/src/modules/panels/TreeDesignerView';
import TreeTest, {ComponentProps as TreeTestComponentProps} from '~/frontend/src/modules/panels/TreeTest';
import TreeView, {ComponentProps as TreeViewComponentProps} from '~/frontend/src/modules/panels/TreeView';
import ViewDesigner, {ComponentProps as ViewDesignerComponentProps} from '~/frontend/src/modules/panels/ViewDesigner';
import XmlImport, {ComponentProps as XmlImportComponentProps} from '~/frontend/src/modules/panels/XmlImport';

type Writeable<T> = { -readonly [P in keyof T] : T[P] };

export const panelClasses = {
    BusinessProcessDesignerView,
    ChartDesignerView,
    FinderDetail,
    FinderOverview,
    Form,
    FormDesignerView,
    GraphDesignerView,
    MapSearchView,
    MetaModelDesignerView,
    Overview,
    OverviewDesignerView,
    QueryExecute,
    TreeDesignerView,
    TreeTest,
    TreeView,
    ViewDesigner,
    XmlImport
};
export type PanelType = keyof typeof panelClasses;

export type PanelProps = {
    [ key : string ] : any;
};

type CommonPanelProps = {
    panelTabTitle? : string;
    panelTabIcon? : string;
    panelTooltip? : React.ReactNode;
};

type CommonCreateNewItem = {
    key? : string;
    name? : string;
    statusId? : number;
    mapping? : { // mapping of the new item attributes to panelProps
        id? : string;
        key? : string;
        name? : string;
        itemTypeId? : string;
        itemTypeName? : string;
    };
};
type CreateNewItem = (
        {
            itemTypeId : number;
            itemTypeName? : never;
        }
        |
        {
            itemTypeName : string;
            itemTypeId? : never;
        }
        )
    & CommonCreateNewItem;


export type BusinessProcessDesignerViewPanelProps = {
        panelType : 'BusinessProcessDesignerView';
        createNewItem? : never;
    }
    & CommonPanelProps
    & Writeable<Omit<BusinessProcessDesignerViewComponentProps, 'uid'>>;
export type ChartDesignerPanelProps = {
        panelType : 'ChartDesignerView';
        createNewItem? : never;
    }
    & CommonPanelProps
    & Writeable<Omit<ChartDesignerViewComponentProps, 'uid'>>;
export type FinderDetailPanelProps = {
        panelType : 'FinderDetail';
        createNewItem? : CreateNewItem;
    }
    & CommonPanelProps
    & Writeable<Omit<FinderDetailComponentProps, 'uid'>>;
export type FinderOverviewPanelProps = {
        panelType : 'FinderOverview';
        createNewItem? : never;
    }
    & CommonPanelProps
    & Writeable<Omit<FinderOverviewComponentProps, 'uid' | 'dataContext' | 'reload'>>;
export type FormPanelProps = {
        panelType : 'Form';
        createNewItem? : CreateNewItem;
    }
    & CommonPanelProps
    & Writeable<Omit<FormComponentProps, 'uid'>>;
export type FormDesignerViewPanelProps = {
        panelType : 'FormDesignerView';
        createNewItem? : never;
    }
    & CommonPanelProps
    & Writeable<Omit<FormDesignerViewComponentProps, 'uid'>>;
export type GraphDesignerViewPanelProps = {
        panelType : 'GraphDesignerView';
        createNewItem? : never;
    }
    & CommonPanelProps
    & Writeable<Omit<GraphDesignerViewComponentProps, 'uid'>>;
export type MapSearchViewPanelProps = {
        panelType : 'MapSearchView';
        createNewItem? : never;
    }
    & CommonPanelProps
    & Writeable<Omit<MapSearchViewComponentProps, 'uid'>>;
export type MetaModelDesignerViewPanelProps = {
        panelType : 'MetaModelDesignerView';
        createNewItem? : never;
    }
    & CommonPanelProps
    & Writeable<Omit<MetaModelDesignerViewProps, 'uid'>>;
export type OverviewPanelProps = {
        panelType : 'Overview';
        createNewItem? : never;
    }
    & CommonPanelProps
    & Writeable<Omit<OverviewComponentProps, 'uid'>>;
export type OverviewDesignerViewPanelProps = {
        panelType : 'OverviewDesignerView';
        createNewItem? : never;
    }
    & CommonPanelProps
    & Writeable<Omit<OverviewDesignerViewComponentProps, 'uid'>>;
export type QueryExecutePanelProps = {
        panelType : 'QueryExecute';
        createNewItem? : never;
    }
    & CommonPanelProps
    & Writeable<Omit<QueryExecuteComponentProps, 'uid'>>;
export type TreeDesignerViewPanelProps = {
        panelType : 'TreeDesignerView';
        createNewItem? : never;
    }
    & CommonPanelProps
    & Writeable<Omit<TreeDesignerViewComponentProps, 'uid'>>;
export type TreeTestPanelProps = {
        panelType : 'TreeTest';
        createNewItem? : never;
    }
    & CommonPanelProps
    & Writeable<Omit<TreeTestComponentProps, 'uid'>>;
export type TreeViewPanelProps = {
        panelType : 'TreeView';
        createNewItem? : CreateNewItem;
    }
    & CommonPanelProps
    & Writeable<Omit<TreeViewComponentProps, 'uid'>>;
export type ViewDesignerPanelProps = {
        panelType : 'ViewDesigner';
        createNewItem? : CreateNewItem;
    }
    & CommonPanelProps
    & Writeable<Omit<ViewDesignerComponentProps, 'uid'>>;
export type XmlImportPanelProps = {
        panelType : 'XmlImport';
        createNewItem? : never;
    }
    & CommonPanelProps
    & Writeable<Omit<XmlImportComponentProps, 'uid'>>;

export type PanelPropsByPanelType = BusinessProcessDesignerViewPanelProps
    | ChartDesignerPanelProps
    | FinderDetailPanelProps
    | FinderOverviewPanelProps
    | FormPanelProps
    | FormDesignerViewPanelProps
    | GraphDesignerViewPanelProps
    | MapSearchViewPanelProps
    | MetaModelDesignerViewPanelProps
    | OverviewPanelProps
    | OverviewDesignerViewPanelProps
    | QueryExecutePanelProps
    | TreeDesignerViewPanelProps
    | TreeTestPanelProps
    | TreeViewPanelProps
    | ViewDesignerPanelProps
    | XmlImportPanelProps;

export interface PanelClass extends React.Component<any, any> {
    panelPropsSchema : JoiObjectSchema;
    panelTabTitle? : string;
    panelTabIcon? : string;
    panelTooltip? : React.ReactNode;
    panelPropsSchemaMsgIdPrefix? : string;

    defaultProps? : PanelProps;

    subPanelProps? : { [ subPanelKey : string ] : PanelProps };

    onPreInitializePanel? : ( props : PanelProps & { user : User } ) => PanelProps;
    onInitializePanel? : ( props : PanelProps & { user : User } ) => PanelProps;
    onClosePanelAction? : ( props : PanelProps & { dontClearData: boolean } ) => ( dispatch : AppDispatch ) => void | Promise<void>;
    onSavePanelAction? : ( props : PanelProps ) => ( dispatch : AppDispatch ) => boolean | Promise<boolean>;
    onSaveAndClosePanelAction? : ( props : PanelProps ) => ( dispatch : AppDispatch ) => void | Promise<void>;
    buildPanelPathFromProps? : ( props : PanelProps ) => string;
    buildPanelPropsFromPath? : ( path : string, historyNavigation : boolean ) => Promise<PanelProps>;
}

export type PanelConfig = {
    panelProps : PanelProps;
    showConfirmCloseDialog : boolean;
    showDeleteSuccessDialog : boolean;
    showPanelPropsDialog : boolean;
    isDirty? : boolean;
    isInvalid? : boolean;
    isBlocking? : boolean;
    dontClearData?: boolean;
    path : string;

    [ key : string ] : any;
};
